import React from 'react';
import { Link } from 'react-router-dom';
import { DocumentTextIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const TermsOfService = () => {
  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-gray-50">
      {/* Header - fixed */}
      <div className="flex-shrink-0 bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="w-8 h-8 text-green-600" />
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
          </div>
          <p className="text-gray-600 mt-2">Last updated: February 2026</p>
        </div>
      </div>

      {/* Content - scrolls inside viewport */}
      <div className="flex-1 min-h-0 overflow-y-auto" data-app-scroll-root>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700">
              By accessing or using SmilesFlow ("the Service"), you agree to be bound by these Terms of Service. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">
              SmilesFlow is a platform that allows you to create, customize, and deploy interactive games 
              for events, marketing, and engagement purposes. The Service includes:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Game creation and customization tools</li>
              <li>• QR code generation for game sharing</li>
              <li>• Leaderboard management</li>
              <li>• Analytics and reporting</li>
              <li>• Lead capture functionality</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Credits and Billing</h2>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.1 Free to build</h3>
            <p className="text-gray-700 mb-4">
              You can sign up, build, brand, preview, and edit campaigns at no cost and with no credit
              card required. Publishing a campaign live (so others can play it via your share link or
              QR code) requires a publish credit.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.2 Publish Credits</h3>
            <p className="text-gray-700 mb-4">
              Publish credits are sold individually or in packs. Current pricing is shown at checkout
              (typically £39 for a single credit or £149 for a 5-credit pack). Each credit publishes
              one campaign. Editing or duplicating a published campaign does not consume an additional
              credit.
            </p>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.3 Automatic Renewal</h3>
            <p className="text-gray-700 mb-4">
              Subscriptions automatically renew at the end of each billing period unless cancelled. 
              You can cancel your subscription at any time through your account settings. Cancellation 
              takes effect at the end of the current billing period.
            </p>
            
            <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.4 Team Seats</h3>
            <p className="text-gray-700">
              The Pro plan includes 1 team member seat. The Team plan supports up to 10 team members
              with unlimited workspaces for managing multiple clients or brands.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Paused Accounts</h2>
            <p className="text-gray-700 mb-4">
              When your subscription ends (cancellation or failed payment), your account
              enters a "paused" state:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• <strong>Games become unavailable:</strong> Your games will not be accessible to players via QR codes or links</li>
              <li>• <strong>Admin access:</strong> You can still log in to view your data, but cannot create new games or make changes</li>
              <li>• <strong>7-day grace period:</strong> Your data is retained for 7 days after your account is paused</li>
              <li>• <strong>Data deletion:</strong> After 7 days, all game data, analytics, and leaderboard entries will be permanently deleted</li>
              <li>• <strong>Reactivation:</strong> You can reactivate your subscription at any time during the grace period to restore full access</li>
            </ul>
          </section>

          <section className="mb-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Refund Policy</h2>
            <p className="text-gray-700 mb-4">
              We offer a <strong>7-day money-back guarantee</strong> for your first subscription purchase, subject to the following conditions:
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li>• Refund requests must be made within 7 days of your first paid subscription</li>
              <li>• This applies to first-time purchases only, not renewals</li>
              <li>• Refunds are processed to your original payment method within 5-10 business days</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>Refunds may be denied if:</strong>
            </p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li>• You have created more than 3 games during the subscription period</li>
              <li>• Your games have received more than 100 total plays</li>
              <li>• You have exported leaderboard data</li>
              <li>• You have previously received a refund from SmilesFlow</li>
            </ul>
            <p className="text-gray-700">
              To request a refund, email{' '}
              <a href="mailto:support@smilesflow.com" className="text-blue-600 hover:underline">
                support@smilesflow.com
              </a>{' '}
              with your account email and reason for the refund request.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h2>
            <p className="text-gray-700 mb-4">You agree NOT to use the Service to:</p>
            <ul className="space-y-2 text-gray-700 mb-4">
              <li>• Create content that is illegal, harmful, threatening, abusive, defamatory, or offensive</li>
              <li>• Infringe on intellectual property rights of others</li>
              <li>• Distribute spam, malware, or other harmful content</li>
              <li>• Attempt to gain unauthorized access to the Service or other accounts</li>
              <li>• Interfere with or disrupt the Service's infrastructure</li>
              <li>• Collect personal information of players without proper consent</li>
              <li>• Use the Service for any purpose that violates applicable laws</li>
            </ul>
            <p className="text-gray-700">
              <strong>Fair use.</strong> Use of the Service is subject to fair and reasonable use. Excessive play volume, automated traffic, or usage that disproportionately consumes resources may be treated as abuse. We may apply soft limits, throttle, or contact you to discuss usage; persistent abuse may result in suspension or termination.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Content</h2>
            <p className="text-gray-700 mb-4">
              When you create games or content on SmilesFlow:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• You retain ownership of your content</li>
              <li>• You grant us a license to host, display, and distribute your content to operate the Service</li>
              <li>• You are solely responsible for the content you create</li>
              <li>• We may remove content that violates these terms without notice</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Service Availability</h2>
            <p className="text-gray-700">
              We strive to maintain high availability but do not guarantee uninterrupted service. 
              We may temporarily suspend the Service for maintenance, updates, or security reasons. 
              We will make reasonable efforts to provide advance notice of planned maintenance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
            <p className="text-gray-700">
              THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. TO THE MAXIMUM EXTENT 
              PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES AND SHALL NOT BE LIABLE FOR ANY INDIRECT, 
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Account Termination</h2>
            <p className="text-gray-700 mb-4">
              We may suspend or terminate your account if you violate these terms. You may also 
              cancel your account at any time. Upon termination:
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>• Your games will immediately become unavailable to players</li>
              <li>• Your data will be deleted after the 7-day grace period</li>
              <li>• No refunds will be provided for the current billing period</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Support</h2>
            <p className="text-gray-700">
              Support is provided via email only at{' '}
              <a href="mailto:support@smilesflow.com" className="text-blue-600 hover:underline">
                support@smilesflow.com
              </a>. 
              We aim to respond to support requests within 1 business day. We do not offer phone 
              support or on-call assistance.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Privacy</h2>
            <p className="text-gray-700">
              Your privacy is important to us. Please review our{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>{' '}
              to understand how we collect, use, and protect your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Changes to Terms</h2>
            <p className="text-gray-700">
              We may update these terms from time to time. We will notify users of significant 
              changes via email or through the Service. Continued use of the Service after changes 
              constitutes acceptance of the updated terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Governing Law</h2>
            <p className="text-gray-700">
              These terms are governed by the laws of the United Kingdom. Any disputes shall be 
              resolved in the courts of England and Wales.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">15. Contact Us</h2>
            <p className="text-gray-700">
              Questions about these terms? Contact us at{' '}
              <a href="mailto:support@smilesflow.com" className="text-blue-600 hover:underline">
                support@smilesflow.com
              </a>
            </p>
          </section>

          <div className="border-t pt-6 mt-8">
            <p className="text-sm text-gray-500">
              These terms are effective as of February 2026 and remain in effect until changed.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default TermsOfService;
